/**
 * DBN EIB Facility — Complete Facility Management Workspace
 * Covenants · Tranches · Allocation · Reporting Calendar · AI Narrative
 */
import React, { useState, useEffect } from "react";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer, ReferenceLine } from "recharts";
import { dbnApi, KPICard, SectionHeader, ProgressBar, Badge, PageHeader,
  LoadingState, TrafficLight, fmtEur, fmtN, fmtPct } from "./dbnUtils";
import { CheckCircle2, AlertTriangle, Clock, ChevronDown, ChevronUp, Shield, FileText } from "lucide-react";

// ── Pace Chart Tooltip ────────────────────────────────────────────────────────
function PaceTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background:"#0a1628", border:"1px solid #1e3a5f", borderRadius:10, padding:"10px 14px", fontSize:10 }}>
      <p style={{ fontWeight:700, color:"#f0f9ff", marginBottom:6 }}>{label}</p>
      {payload.map(p => (
        <p key={p.name} style={{ color:p.color }}>
          {p.name}: {fmtEur(p.value || 0)}
        </p>
      ))}
    </div>
  );
}

// ── Covenant Status Row ───────────────────────────────────────────────────────
function CovenantRow({ cov }) {
  const [showAction, setShowAction] = useState(false);
  const statusColor = cov.status === "Compliant" ? "#22c55e" :
                      cov.status === "Warning"   ? "#f59e0b" : "#ef4444";
  return (
    <div style={{ background:"#060f1e", borderRadius:10, padding:"10px 12px", marginBottom:6 }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}
        onClick={() => setShowAction(!showAction)} style={{ cursor:"pointer" }}>
        <div style={{ display:"flex", alignItems:"center", gap:8, flex:1, cursor:"pointer" }}
          onClick={() => setShowAction(!showAction)}>
          <div style={{ width:8, height:8, borderRadius:2, background:statusColor, flexShrink:0 }} />
          <p style={{ fontSize:10, color:"#94a3b8", fontWeight:600 }}>{cov.covenant}</p>
          {cov.critical && <Badge label="Critical" color="#ef4444" />}
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          {cov.target && typeof cov.current === "number" && (
            <p style={{ fontSize:9, color:"#475569" }}>
              Target {typeof cov.target === "number" && cov.target < 10 ? `${cov.target}x` : `${cov.target}%`} | Current {typeof cov.current === "number" && cov.current < 10 ? `${cov.current}x` : `${cov.current}%`}
            </p>
          )}
          <Badge label={cov.status} color={statusColor} />
          {cov.status !== "Compliant" && <AlertTriangle size={12} color={statusColor} />}
          {cov.status === "Compliant" && <CheckCircle2 size={12} color="#22c55e" />}
          {showAction ? <ChevronUp size={12} color="#475569" /> : <ChevronDown size={12} color="#475569" />}
        </div>
      </div>
      {showAction && cov.action && (
        <div style={{ marginTop:8, background:"#0a1628", borderRadius:7, padding:"7px 10px",
          borderLeft:`2px solid ${statusColor}` }}>
          <p style={{ fontSize:9, color:"#64748b", lineHeight:1.5 }}>{cov.action}</p>
        </div>
      )}
    </div>
  );
}

// ── Tranche Card ──────────────────────────────────────────────────────────────
function TrancheCard({ t }) {
  const [open, setOpen] = useState(false);
  const drawPct = t.amount > 0 ? Math.round(t.disbursed / t.amount * 100) : 0;
  const commitPct = t.amount > 0 ? Math.round(t.committed / t.amount * 100) : 0;
  return (
    <div style={{ background:"#0a1628", border:`1px solid ${t.color}30`, borderRadius:14,
      padding:16, borderTop:`3px solid ${t.color}` }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:10 }}>
        <div>
          <p style={{ fontSize:13, fontWeight:800, color:t.color }}>{t.label}</p>
          <p style={{ fontSize:10, color:"#475569" }}>{t.id}</p>
        </div>
        <div style={{ textAlign:"right" }}>
          <p style={{ fontSize:18, fontWeight:900, color:"#f0f9ff" }}>{fmtEur(t.amount)}</p>
          <Badge label={t.status} color={t.color} />
        </div>
      </div>
      {[
        { l:"Committed", v:t.committed, pct:commitPct, c:"#a78bfa" },
        { l:"Disbursed", v:t.disbursed, pct:drawPct,   c:t.color    },
        { l:"Available", v:t.available, pct:t.amount>0?Math.round(t.available/t.amount*100):0, c:"#334155" },
      ].map(x => (
        <div key={x.l} style={{ marginBottom:7 }}>
          <div style={{ display:"flex", justifyContent:"space-between", marginBottom:2 }}>
            <span style={{ fontSize:9, color:"#64748b" }}>{x.l}</span>
            <span style={{ fontSize:9, fontWeight:700, color:x.c }}>{fmtEur(x.v)} ({x.pct}%)</span>
          </div>
          <ProgressBar value={x.pct} color={x.c} height={4} showPct={false} />
        </div>
      ))}
      {t.signed && <p style={{ fontSize:8, color:"#334155", marginTop:6 }}>Signed: {t.signed} · Expiry: {t.expiry}</p>}
      <p style={{ fontSize:8.5, color:"#64748b", marginTop:6, lineHeight:1.5 }}>{t.note}</p>
      <button onClick={() => setOpen(!open)}
        style={{ background:"none", border:"none", color:"#475569", cursor:"pointer", fontSize:9, marginTop:6,
          display:"flex", alignItems:"center", gap:4 }}>
        {open ? <ChevronUp size={10}/> : <ChevronDown size={10}/>}
        Conditions precedent ({t.conditions?.length || 0})
      </button>
      {open && (
        <div style={{ marginTop:6 }}>
          {(t.conditions||[]).map((cond, i) => (
            <div key={i} style={{ display:"flex", alignItems:"center", gap:6, marginBottom:4 }}>
              <div style={{ width:6, height:6, borderRadius:"50%",
                background:t.conditions_met ? "#22c55e" : i < (t.conditions.length - t.conditions_outstanding) ? "#22c55e" : "#f59e0b",
                flexShrink:0 }} />
              <span style={{ fontSize:8.5, color:"#64748b" }}>{cond}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Calendar Row ──────────────────────────────────────────────────────────────
function CalendarRow({ item }) {
  const statusColors = { Overdue:"#ef4444","In Progress":"#f59e0b",Scheduled:"#60a5fa",Pending:"#475569",Complete:"#22c55e" };
  const c = statusColors[item.status] || "#475569";
  return (
    <div style={{ display:"flex", alignItems:"flex-start", gap:12, padding:"8px 0",
      borderBottom:"1px solid #0f1e33" }}>
      <div style={{ width:80, flexShrink:0 }}>
        <p style={{ fontSize:10, fontWeight:700, color:c }}>{item.due}</p>
        <Badge label={item.status} color={c} />
      </div>
      <div style={{ flex:1 }}>
        <p style={{ fontSize:10, fontWeight:700, color:"#f0f9ff" }}>{item.obligation}</p>
        <p style={{ fontSize:9, color:"#475569" }}>{item.responsible} · {item.notes}</p>
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function DBNEIBFacility() {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    dbnApi.facilityDetail("FAC-EIB")
      .then(r => setDetail(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingState label="Loading EIB facility data…" />;
  if (!detail) return null;

  const f = detail.facility || {};
  const tranches = detail.tranches || [];
  const covenants = detail.covenants || [];
  const cs = detail.covenant_summary || {};
  const pace = detail.pace_data || [];
  const alloc = detail.allocation_tracking || {};
  const cal = detail.reporting_calendar || [];
  const sectorAlloc = detail.sector_allocation || [];
  const pfiAlloc = detail.pfi_allocation || [];
  const stateAlloc = detail.state_allocation || [];

  const handleGenerateReport = async (fmt) => {
    setGenerating(true);
    const url = dbnApi.reportDownload("eib-quarterly", fmt);
    window.open(url, "_blank");
    setTimeout(() => setGenerating(false), 2000);
  };

  const TABS = [
    {key:"overview",   label:"Overview"},
    {key:"covenants",  label:`Covenants (${cs.breach||0} Breach / ${cs.warning||0} Warning)`},
    {key:"tranches",   label:"Tranche Management"},
    {key:"allocation", label:"Allocation Tracking"},
    {key:"calendar",   label:"Reporting Calendar"},
    {key:"narrative",  label:"AI Facility Brief"},
  ];

  return (
    <div style={{ display:"flex", flexDirection:"column", minHeight:"100%" }}>
      <PageHeader
        title="Development Partner Facilities"
        subtitle="Facility management workspace · Covenants · Tranches · Allocation · Reporting">
        <Badge label="Active" color="#22c55e" />
        <Badge label="€200M" color="#60a5fa" />
        {cs.breach > 0 && <Badge label={`${cs.breach} Covenant Breach`} color="#ef4444" />}
        {cs.warning > 0 && <Badge label={`${cs.warning} Warning`} color="#f59e0b" />}
      </PageHeader>

      <div style={{ padding:"16px 24px", flex:1 }}>
        {/* Hero metrics — always visible */}
        <div style={{ background:"linear-gradient(135deg,#0a1628,#071124)", border:"1px solid #0f2033",
          borderRadius:16, padding:"16px 20px", marginBottom:14 }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>
            <div>
              <p style={{ fontSize:11, color:"#34d399", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.1em" }}>European Investment Bank</p>
              <p style={{ fontSize:17, fontWeight:900, color:"#f0f9ff" }}>EIB–DBN Green and Digital MSME and Mid-Cap Facility</p>
              <p style={{ fontSize:10, color:"#475569" }}>Availability period: 1 July 2024 – 30 June 2027 · Signed Agreement ref: EIB-DBN/24/001</p>
            </div>
            <div style={{ textAlign:"right" }}>
              <p style={{ fontSize:28, fontWeight:900, color:"#34d399" }}>€200M</p>
              <p style={{ fontSize:10, color:"#64748b" }}>Total facility value</p>
            </div>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(6,1fr)", gap:10 }}>
            {[
              {l:"Available",       v:fmtEur(f.available),     sub:"Unallocated"},
              {l:"Allocated",       v:fmtEur(f.allocated),     sub:"To PFIs"},
              {l:"Committed",       v:fmtEur(f.committed),     sub:"Signed agreements"},
              {l:"Disbursed",       v:fmtEur(f.disbursed),     sub:`${Math.round(f.disbursed/f.total_value*100)}% of total`},
              {l:"Verified Eligible",v:fmtEur(f.verified_eligible), sub:"Post-verification"},
              {l:"Impact Reported", v:fmtEur(f.impact_reported), sub:"Q3 2025"},
            ].map(m => (
              <div key={m.l} style={{ background:"rgba(255,255,255,0.03)", borderRadius:10, padding:"10px 12px", textAlign:"center" }}>
                <p style={{ fontSize:14, fontWeight:900, color:"#f0f9ff" }}>{m.v}</p>
                <p style={{ fontSize:9, color:"#64748b", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.07em" }}>{m.l}</p>
                <p style={{ fontSize:8, color:"#334155" }}>{m.sub}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Covenant summary bar */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10, marginBottom:14 }}>
          {[
            {l:"Covenants Compliant", v:cs.compliant, total:cs.total, c:"#22c55e"},
            {l:"Warnings",            v:cs.warning,   total:cs.total, c:"#f59e0b"},
            {l:"Breaches",            v:cs.breach,    total:cs.total, c:"#ef4444"},
          ].map(x => (
            <div key={x.l} style={{ background:"#0a1628", border:`1px solid ${x.v>0?x.c+"30":"#0f2033"}`,
              borderLeft:`3px solid ${x.c}`, borderRadius:10, padding:"10px 14px",
              display:"flex", alignItems:"center", gap:12 }}>
              <p style={{ fontSize:22, fontWeight:900, color:x.c }}>{x.v}</p>
              <div>
                <p style={{ fontSize:9, color:"#475569", textTransform:"uppercase", letterSpacing:"0.08em" }}>{x.l}</p>
                <p style={{ fontSize:8, color:"#334155" }}>of {x.total} covenants</p>
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div style={{ display:"flex", gap:2, marginBottom:14, background:"#0a1628",
          border:"1px solid #0f2033", borderRadius:10, padding:4, overflowX:"auto", width:"fit-content" }}>
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              style={{ padding:"7px 14px", borderRadius:8, border:"none", cursor:"pointer", fontSize:11, fontWeight:700,
                background:tab===t.key?"rgba(52,211,153,0.15)":"transparent",
                color:tab===t.key?"#34d399":"#475569", whiteSpace:"nowrap" }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* ── Overview Tab ── */}
        {tab === "overview" && (
          <div>
            {/* Deployment waterfall */}
            <div style={{ background:"#0a1628", border:"1px solid #0f2033", borderRadius:14, padding:16, marginBottom:14 }}>
              <SectionHeader title="Capital Deployment Waterfall" sub="From facility signing to verified impact reporting" />
              {[
                {label:"Facility Signed",   value:200_000_000, pct:100,  color:"#22c55e"},
                {label:"Allocated to PFIs", value:123_000_000, pct:61.5, color:"#60a5fa"},
                {label:"Committed",         value:95_000_000,  pct:47.5, color:"#a78bfa"},
                {label:"Disbursed",         value:72_400_000,  pct:36.2, color:"#34d399"},
                {label:"Verified Eligible", value:61_200_000,  pct:30.6, color:"#0d9488"},
                {label:"Impact Reported",   value:55_000_000,  pct:27.5, color:"#059669"},
              ].map(s => (
                <div key={s.label} style={{ display:"flex", alignItems:"center", gap:10, marginBottom:8 }}>
                  <span style={{ width:180, fontSize:10, color:"#94a3b8", fontWeight:600 }}>{s.label}</span>
                  <div style={{ flex:1, height:8, background:"#0f2033", borderRadius:4 }}>
                    <div style={{ height:8, width:`${s.pct}%`, background:`linear-gradient(90deg,${s.color},${s.color}cc)`, borderRadius:4 }} />
                  </div>
                  <span style={{ width:90, fontSize:11, fontWeight:700, color:s.color, textAlign:"right" }}>{fmtEur(s.value)}</span>
                  <span style={{ width:40, fontSize:10, color:"#475569", textAlign:"right" }}>{s.pct}%</span>
                </div>
              ))}
            </div>

            {/* Impact */}
            <div style={{ background:"#0a1628", border:"1px solid #0f2033", borderRadius:14, padding:16 }}>
              <SectionHeader title="Development Impact — EIB Facility" sub="Q3 2025 · Verified and estimated indicators" />
              <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:10 }}>
                {[
                  {l:"MSMEs Financed",      v:"2,840", note:"Direct end beneficiaries"},
                  {l:"Mid-Caps",            v:"42",    note:"€500K–€12.5M tickets"},
                  {l:"Jobs Supported",      v:"22,400",note:"Direct + indirect"},
                  {l:"Women-led",           v:"26%",   note:"Target: 30% — below target"},
                  {l:"Emissions Avoided",   v:"38,200 t",note:"tCO₂e · estimated"},
                  {l:"Renewable Energy",    v:"12.4 GWh",note:"Generated annually"},
                  {l:"Rural Beneficiaries", v:"8,600", note:"Outside major metros"},
                  {l:"Digital Users",       v:"5,900", note:"Digital-project reach"},
                  {l:"Private Capital Mob.",v:fmtEur(101_400_000),note:"1.4x ratio"},
                  {l:"Green Allocation",    v:"54%",   note:"Target ≥55% — 1pp shortfall"},
                ].map(m => (
                  <div key={m.l} style={{ background:"#060f1e", borderRadius:10, padding:"10px 12px" }}>
                    <p style={{ fontSize:14, fontWeight:900, color:"#f0f9ff" }}>{m.v}</p>
                    <p style={{ fontSize:9, color:"#475569", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.07em", marginTop:2 }}>{m.l}</p>
                    <p style={{ fontSize:8, color:"#334155", marginTop:1 }}>{m.note}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── Covenants Tab ── */}
        {tab === "covenants" && (
          <div>
            <p style={{ fontSize:10, color:"#475569", marginBottom:14, lineHeight:1.6 }}>
              Click any covenant row to see the recommended remediation action.
              Red = covenant breach (immediate action required) · Amber = approaching threshold · Green = compliant.
            </p>
            {covenants.map(cov => <CovenantRow key={cov.id} cov={cov} />)}
          </div>
        )}

        {/* ── Tranches Tab ── */}
        {tab === "tranches" && (
          <div>
            <div style={{ background:"#001a0a", border:"1px solid #004a1e", borderRadius:8,
              padding:"8px 14px", marginBottom:14, display:"flex", alignItems:"center", gap:8 }}>
              <Shield size={11} color="#34d399" />
              <p style={{ fontSize:9, color:"#065f46" }}>
                All tranche figures reconcile: Total €{(200).toFixed(0)}M = Tranche A €75M + Tranche B €80M + Tranche C €45M.
                Committed €95M and Disbursed €72.4M verified against facility ledger.
              </p>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12 }}>
              {tranches.map(t => <TrancheCard key={t.id} t={t} />)}
            </div>
          </div>
        )}

        {/* ── Allocation Tracking Tab ── */}
        {tab === "allocation" && (
          <div>
            {/* Green / Digital / Gender */}
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12, marginBottom:14 }}>
              {[
                {key:"green",   label:"Green Allocation",   target_label:"EIB Covenant: ≥55%", color:"#34d399", icon:"🌱"},
                {key:"digital", label:"Digital Allocation",  target_label:"EIB Covenant: ≥25%", color:"#60a5fa", icon:"💻"},
                {key:"gender",  label:"Gender Allocation",   target_label:"EIB Covenant: ≥30%", color:"#ec4899", icon:"👩"},
              ].map(x => {
                const a = alloc[x.key] || {};
                const statusColor = a.status === "Breach" ? "#ef4444" : a.status === "Warning" ? "#f59e0b" : "#22c55e";
                return (
                  <div key={x.key} style={{ background:"#0a1628", border:`1px solid ${statusColor}30`,
                    borderTop:`3px solid ${statusColor}`, borderRadius:12, padding:16 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", marginBottom:8 }}>
                      <p style={{ fontSize:12, fontWeight:700, color:"#f0f9ff" }}>{x.label}</p>
                      <Badge label={a.status||"Compliant"} color={statusColor} />
                    </div>
                    <p style={{ fontSize:28, fontWeight:900, color:statusColor }}>{a.current_pct}%</p>
                    <p style={{ fontSize:9, color:"#475569", marginBottom:8 }}>{x.target_label} | Current: {a.current_pct}%</p>
                    <ProgressBar value={a.current_pct} max={a.target_pct * 1.5} color={statusColor} height={6} showPct={false} />
                    <div style={{ display:"flex", justifyContent:"space-between", marginTop:6 }}>
                      <span style={{ fontSize:9, color:"#334155" }}>Amount: {fmtEur(a.amount)}</span>
                      {a.gap_pct > 0 && <span style={{ fontSize:9, color:statusColor }}>Gap: {a.gap_pct}pp</span>}
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12 }}>
              {/* Sector allocation */}
              <div style={{ background:"#0a1628", border:"1px solid #0f2033", borderRadius:12, padding:14 }}>
                <SectionHeader title="Sector Allocation" sub="By finance amount" />
                {sectorAlloc.slice(0,7).map(s => (
                  <div key={s.sector} style={{ display:"flex", alignItems:"center", gap:8, marginBottom:7 }}>
                    <span style={{ fontSize:9, color:"#64748b", flex:1 }}>{s.sector.split(" ")[0]}</span>
                    <div style={{ width:80, height:5, background:"#0f2033", borderRadius:3 }}>
                      <div style={{ height:5, width:`${s.pct}%`, background:"#34d399", borderRadius:3 }} />
                    </div>
                    <span style={{ fontSize:9, color:"#64748b", width:32, textAlign:"right" }}>{s.pct}%</span>
                  </div>
                ))}
              </div>

              {/* PFI allocation */}
              <div style={{ background:"#0a1628", border:"1px solid #0f2033", borderRadius:12, padding:14 }}>
                <SectionHeader title="PFI Allocation" sub="By committed amount" />
                {pfiAlloc.map(p => (
                  <div key={p.pfi_id} style={{ display:"flex", alignItems:"center", gap:8, marginBottom:7 }}>
                    <span style={{ fontSize:9, color:"#64748b", flex:1 }}>{p.pfi_name.split(" ")[0]}</span>
                    <div style={{ width:60, height:5, background:"#0f2033", borderRadius:3 }}>
                      <div style={{ height:5, width:`${p.pct}%`, background:"#60a5fa", borderRadius:3 }} />
                    </div>
                    <span style={{ fontSize:9, color:"#64748b", width:32, textAlign:"right" }}>{p.pct}%</span>
                  </div>
                ))}
              </div>

              {/* Geographic allocation */}
              <div style={{ background:"#0a1628", border:"1px solid #0f2033", borderRadius:12, padding:14 }}>
                <SectionHeader title="Geographic Allocation" sub="By state" />
                {stateAlloc.map(s => (
                  <div key={s.state} style={{ display:"flex", alignItems:"center", gap:8, marginBottom:7 }}>
                    <span style={{ fontSize:9, color:"#64748b", flex:1 }}>{s.state}</span>
                    <div style={{ width:60, height:5, background:"#0f2033", borderRadius:3 }}>
                      <div style={{ height:5, width:`${s.pct}%`, background:"#a78bfa", borderRadius:3 }} />
                    </div>
                    <span style={{ fontSize:9, color:"#64748b", width:32, textAlign:"right" }}>{s.pct}%</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Disbursement pace chart */}
            <div style={{ background:"#0a1628", border:"1px solid #0f2033", borderRadius:14, padding:16, marginTop:14 }}>
              <SectionHeader title="Disbursement Pace vs Target" sub="Quarterly cumulative — target: 45% annual disbursement pace" />
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={pace} margin={{ top:10, right:20, bottom:4, left:10 }}>
                  <CartesianGrid stroke="#0f2033" strokeDasharray="3 3" />
                  <XAxis dataKey="quarter" tick={{ fill:"#475569", fontSize:9 }} />
                  <YAxis tickFormatter={v => `€${(v/1e6).toFixed(0)}M`} tick={{ fill:"#475569", fontSize:9 }} />
                  <Tooltip content={<PaceTooltip />} />
                  <Legend wrapperStyle={{ fontSize:10, color:"#475569" }} />
                  <Line type="monotone" dataKey="cumulative_target" name="Target" stroke="#f59e0b" strokeDasharray="5 5" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="cumulative_actual" name="Actual" stroke="#34d399" strokeWidth={2} dot={{ fill:"#34d399", r:4 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* ── Reporting Calendar ── */}
        {tab === "calendar" && (
          <div style={{ background:"#0a1628", border:"1px solid #0f2033", borderRadius:14, padding:16 }}>
            <SectionHeader title="EIB Reporting Obligations Calendar" sub="Upcoming obligations, due dates and status" />
            {cal.map((item, i) => <CalendarRow key={i} item={item} />)}
            <div style={{ marginTop:14, background:"#001a0a", border:"1px solid #004a1e",
              borderRadius:8, padding:"8px 12px" }}>
              <p style={{ fontSize:9, color:"#065f46" }}>
                Next priority: Q3 2025 Interim Report is overdue. Escalate to DBN Sustainable Finance Division immediately.
                Failure to submit within 15 business days may trigger an EIB review notification.
              </p>
            </div>
          </div>
        )}

        {/* ── AI Facility Brief ── */}
        {tab === "narrative" && (
          <div>
            <div style={{ background:"#0a1628", border:"1px solid #0f2033", borderRadius:14, padding:16, marginBottom:14 }}>
              <SectionHeader title="AI-Generated Facility Narrative" sub="Based on live portfolio data · Updated daily · For internal use" />
              <div style={{ background:"#060f1e", borderRadius:10, padding:"14px 16px",
                borderLeft:"3px solid #34d399" }}>
                <p style={{ fontSize:10, color:"#94a3b8", lineHeight:1.8 }}>{detail.ai_narrative}</p>
              </div>
              <div style={{ marginTop:12, display:"flex", gap:8 }}>
                {["pdf","excel","word"].map(fmt => (
                  <button key={fmt} onClick={() => handleGenerateReport(fmt)}
                    data-testid={`generate-report-${fmt}`}
                    style={{ padding:"6px 12px", borderRadius:8, fontSize:10, fontWeight:700, cursor:"pointer",
                      background:"transparent", border:"1px solid #1e3a5f", color:"#93c5fd" }}>
                    {generating ? "Generating…" : `Export ${fmt.toUpperCase()}`}
                  </button>
                ))}
              </div>
            </div>

            {/* Facility risk summary */}
            <div style={{ background:"#0a1628", border:"1px solid #0f2033", borderRadius:14, padding:16 }}>
              <SectionHeader title="Facility Risk Summary" sub="Key risks requiring management attention" />
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                {[
                  {risk:"Deployment Pace",       severity:"High",   desc:"Disbursement at 36.2% vs 45% annual target. Q4 acceleration required.",           action:"Fast-track PRJ-005, PRJ-019, PRJ-008"},
                  {risk:"Green Covenant",         severity:"High",   desc:"54% allocation vs 55% target — 1pp shortfall threatens covenant compliance.",     action:"Prioritise green-eligible disbursements"},
                  {risk:"Gender Covenant Breach", severity:"Critical",desc:"26% vs 30% target. Active breach requiring PFI engagement programme.",           action:"Engage PFIs 001, 002, 005 immediately"},
                  {risk:"Tranche C Availability", severity:"Medium", desc:"Tranche C conditions not met. Tranche B utilisation at 25% vs 50% required.",    action:"Target Tranche B milestone Q1 2026"},
                  {risk:"Reporting Overdue",      severity:"High",   desc:"Q3 2025 interim report overdue. Risk of EIB escalation.",                         action:"Submit within 15 business days"},
                  {risk:"PFI Data Quality",       severity:"Low",    desc:"3 PFIs below 75% data quality threshold affecting impact reporting.",             action:"Assign TA for data quality improvement"},
                ].map(r => {
                  const sc = r.severity==="Critical"?"#ef4444":r.severity==="High"?"#f97316":r.severity==="Medium"?"#f59e0b":"#60a5fa";
                  return (
                    <div key={r.risk} style={{ background:"#060f1e", borderRadius:10, padding:"10px 12px",
                      borderLeft:`3px solid ${sc}` }}>
                      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                        <p style={{ fontSize:10, fontWeight:700, color:"#f0f9ff" }}>{r.risk}</p>
                        <Badge label={r.severity} color={sc} />
                      </div>
                      <p style={{ fontSize:9, color:"#64748b", marginBottom:4, lineHeight:1.4 }}>{r.desc}</p>
                      <p style={{ fontSize:8.5, color:sc }}>Action: {r.action}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
